import React, { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './LoginPage.css';

// ─── Tipos de modal ───────────────────────────────────────────────────────────
type ModalTipo = 'senha' | 'login' | null;

// ─── Modal: Esqueci a Senha ───────────────────────────────────────────────────
function ModalEsqueciSenha({ onFechar }: { onFechar: () => void }) {
  const [emailRec, setEmailRec] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState('');
  const [erro, setErro] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setMsg('');
    if (!emailRec.trim()) { setErro('Informe seu e-mail.'); return; }
    setEnviando(true);
    try {
      const res = await api.post<{ mensagem: string }>('/auth/recuperar-senha', { email: emailRec.trim() });
      setMsg(res.data.mensagem);
    } catch (error) {
      if (isAxiosError(error) && !error.response) {
        setErro('Servidor indisponível. Ative o modo demonstração ou inicie o backend.');
      } else {
        setErro('Não foi possível processar sua solicitação. Tente novamente.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="lp-overlay" onClick={onFechar}>
      <div className="lp-modal" onClick={e => e.stopPropagation()}>
        <button className="lp-modal-fechar" onClick={onFechar} aria-label="Fechar">✕</button>
        <div className="lp-modal-icone">🔑</div>
        <h2 className="lp-modal-titulo">Esqueci minha senha</h2>
        <p className="lp-modal-desc">
          Informe o e-mail cadastrado. Se ele existir no sistema, você receberá instruções para redefinir sua senha.
        </p>

        {msg ? (
          <div className="lp-alerta lp-alerta-ok">
            <span>✅</span> {msg}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="lp-modal-form">
            {erro && <div className="lp-alerta lp-alerta-erro"><span>⚠️</span> {erro}</div>}
            <div className="lp-campo">
              <label htmlFor="rec-email">E-mail de acesso</label>
              <input
                id="rec-email"
                type="email"
                value={emailRec}
                onChange={e => setEmailRec(e.target.value)}
                placeholder="seu@email.com.br"
                autoFocus
                required
              />
            </div>
            <button type="submit" className="lp-btn-primario" disabled={enviando}>
              {enviando ? 'Enviando…' : 'Enviar instruções'}
            </button>
          </form>
        )}

        <button className="lp-link-modal" onClick={onFechar}>
          ← Voltar ao login
        </button>
      </div>
    </div>
  );
}

// ─── Modal: Esqueci o Login ───────────────────────────────────────────────────
function ModalEsqueciLogin({ onFechar }: { onFechar: () => void }) {
  const [cpf, setCpf] = useState('');
  const [nome, setNome] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<{ email_mascarado?: string; mensagem: string } | null>(null);
  const [erro, setErro] = useState('');

  function formatarCpf(v: string) {
    return v.replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, '$1.$2.$3-$4')
      .slice(0, 14);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setResultado(null);
    if (!cpf.trim() && !nome.trim()) {
      setErro('Informe o CPF ou o nome completo cadastrado.');
      return;
    }
    setEnviando(true);
    try {
      const res = await api.post<{ email_mascarado?: string; mensagem: string }>(
        '/auth/recuperar-login',
        { cpf: cpf.replace(/\D/g, '') || undefined, nome: nome.trim() || undefined }
      );
      setResultado(res.data);
    } catch (error) {
      if (isAxiosError(error) && !error.response) {
        setErro('Servidor indisponível. Ative o modo demonstração ou inicie o backend.');
      } else {
        setErro('Não foi possível processar sua solicitação. Tente novamente.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="lp-overlay" onClick={onFechar}>
      <div className="lp-modal" onClick={e => e.stopPropagation()}>
        <button className="lp-modal-fechar" onClick={onFechar} aria-label="Fechar">✕</button>
        <div className="lp-modal-icone">👤</div>
        <h2 className="lp-modal-titulo">Esqueci meu login</h2>
        <p className="lp-modal-desc">
          Informe o <strong>CPF</strong> ou o <strong>nome completo</strong> cadastrado no sistema para
          recuperarmos seu e-mail de acesso.
        </p>

        {resultado ? (
          <div className={`lp-alerta ${resultado.email_mascarado ? 'lp-alerta-ok' : 'lp-alerta-info'}`}>
            <p>{resultado.mensagem}</p>
            {resultado.email_mascarado && (
              <p className="lp-email-mascarado">📧 <strong>{resultado.email_mascarado}</strong></p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="lp-modal-form">
            {erro && <div className="lp-alerta lp-alerta-erro"><span>⚠️</span> {erro}</div>}

            <div className="lp-campo">
              <label htmlFor="rec-cpf">CPF</label>
              <input
                id="rec-cpf"
                type="text"
                value={cpf}
                onChange={e => setCpf(formatarCpf(e.target.value))}
                placeholder="000.000.000-00"
                inputMode="numeric"
                autoFocus
              />
            </div>

            <div className="lp-separador">
              <span>ou</span>
            </div>

            <div className="lp-campo">
              <label htmlFor="rec-nome">Nome completo</label>
              <input
                id="rec-nome"
                type="text"
                value={nome}
                onChange={e => setNome(e.target.value)}
                placeholder="Como está cadastrado no sistema"
              />
            </div>

            <button type="submit" className="lp-btn-primario" disabled={enviando}>
              {enviando ? 'Buscando…' : 'Buscar meu login'}
            </button>
          </form>
        )}

        <button className="lp-link-modal" onClick={onFechar}>
          ← Voltar ao login
        </button>
      </div>
    </div>
  );
}

// ─── Página Principal de Login ────────────────────────────────────────────────
export default function LoginPage() {
  const { login, isCarregando } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [modal, setModal] = useState<ModalTipo>(null);
  const [mostrarSenha, setMostrarSenha] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErro('');
    try {
      await login(email, senha);
      navigate('/app');
    } catch (error) {
      if (isAxiosError(error) && !error.response) {
        setErro('Backend indisponível. Inicie o backend e tente novamente.');
        return;
      }
      setErro('E-mail ou senha inválidos. Verifique suas credenciais.');
    }
  }

  return (
    <>
      <div className="lp-container">
        <form className="lp-card" onSubmit={handleSubmit} noValidate>
          {/* Logo */}
          <div className="lp-logo">
            <span className="lp-logo-icone">🏫</span>
            <h1>Gestão de Creches</h1>
            <p className="lp-logo-subtitulo">Sistema de Administração</p>
          </div>

          {/* Erro */}
          {erro && (
            <div className="lp-alerta lp-alerta-erro" role="alert">
              <span>⚠️</span> {erro}
            </div>
          )}

          {/* Campo e-mail */}
          <div className="lp-campo">
            <label htmlFor="email">E-mail</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="seu@email.com.br"
              required
              autoFocus
              autoComplete="username"
            />
          </div>

          {/* Campo senha */}
          <div className="lp-campo">
            <label htmlFor="senha">
              Senha
            </label>
            <div className="lp-campo-senha">
              <input
                id="senha"
                type={mostrarSenha ? 'text' : 'password'}
                value={senha}
                onChange={e => setSenha(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="lp-toggle-senha"
                onClick={() => setMostrarSenha(v => !v)}
                aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                tabIndex={-1}
              >
                {mostrarSenha ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Botão principal */}
          <button
            type="submit"
            className="lp-btn-primario"
            disabled={isCarregando}
          >
            {isCarregando ? 'Entrando…' : 'Entrar'}
          </button>

          {/* Links de recuperação */}
          <div className="lp-recuperacao">
            <button
              type="button"
              className="lp-link-recuperacao"
              onClick={() => setModal('senha')}
            >
              🔑 Esqueci minha senha
            </button>
            <span className="lp-separador-vertical">|</span>
            <button
              type="button"
              className="lp-link-recuperacao"
              onClick={() => setModal('login')}
            >
              👤 Esqueci meu login
            </button>
          </div>

          {/* Link para SaaS Admin */}
          <div className="lp-saas-link-section">
            <p className="lp-saas-link-text">
              É um administrador SaaS?{' '}
              <Link to="/saas/login" className="lp-saas-link">
                Acesse o portal administrativo
              </Link>
            </p>
          </div>

          {/* Voltar */}
          <Link className="lp-link-voltar" to="/">
            ← Voltar para a página principal
          </Link>
        </form>
      </div>

      {/* Modais */}
      {modal === 'senha' && <ModalEsqueciSenha onFechar={() => setModal(null)} />}
      {modal === 'login' && <ModalEsqueciLogin onFechar={() => setModal(null)} />}
    </>
  );
}
